import { memo, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { dashPerfCursorAtom, dashServerStatsAtom, dashSvRuntimeAtom, useGetDashDataAge } from './dashboardHooks';
import { cn } from '@/lib/utils';
import { TimerIcon, TrendingUpIcon, MemoryStickIcon, ShieldAlertIcon } from 'lucide-react';

const UptimeCircle = ({ pct }: { pct: number }) => {
    const radius = 22;
    const stroke = 3.5;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (pct / 100) * circumference;

    return (
        <svg height={radius * 2} width={radius * 2} className="rotate-[-90deg] shrink-0">
            <circle
                stroke="rgba(255,255,255,0.03)"
                fill="transparent"
                strokeWidth={stroke}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
            />
            <circle
                stroke="var(--sc-green)"
                fill="transparent"
                strokeWidth={stroke}
                strokeDasharray={circumference + ' ' + circumference}
                style={{ strokeDashoffset }}
                strokeLinecap="round"
                r={normalizedRadius}
                cx={radius}
                cy={radius}
                className="transition-all duration-500 ease-out"
            />
        </svg>
    );
};

export default function ServerStatsCard() {
    const pastStatsData = useAtomValue(dashServerStatsAtom);
    const svRuntimeData = useAtomValue(dashSvRuntimeAtom);
    const perfCursorData = useAtomValue(dashPerfCursorAtom);
    const getDashDataAge = useGetDashDataAge();

    const displayData = useMemo(() => {
        const dataAge = getDashDataAge();
        if (!svRuntimeData || dataAge.isExpired) return null;

        if (perfCursorData && perfCursorData.snap) {
            const nodeMem = svRuntimeData.nodeMemory as any;
            return {
                fxsMemory: perfCursorData.snap.fxsMemory,
                nodeMemory: nodeMem && perfCursorData.snap.nodeMemory ? {
                    used: perfCursorData.snap.nodeMemory,
                    limit: nodeMem.limit ?? nodeMem.heapTotal ?? 0,
                } : null,
                isHistorical: true,
            };
        } else {
            const nodeMem = svRuntimeData.nodeMemory as any;
            return {
                fxsMemory: svRuntimeData.fxsMemory,
                nodeMemory: nodeMem ? {
                    used: nodeMem.heapUsed ?? nodeMem.used ?? 0,
                    limit: nodeMem.heapTotal ?? nodeMem.limit ?? 0,
                } : null,
                isHistorical: false,
            };
        }
    }, [svRuntimeData, perfCursorData]);

    const uptimePct = pastStatsData?.uptimePct ?? 0;
    const medianPlayers = pastStatsData?.medianPlayerCount ?? 0;
    const fxsMemory = displayData?.fxsMemory;
    const nodeMemory = displayData?.nodeMemory;

    // Formatting outputs
    const uptimePart = uptimePct ? uptimePct.toFixed(1) + '%' : '--%';
    const medianPlayerPart = medianPlayers ? Math.ceil(medianPlayers) : '0';
    const fxsPart = fxsMemory ? fxsMemory.toFixed(0) + ' MB' : '-- MB';

    let nodePart = '--';
    let nodeMemoryUsage = 0;
    let nodeColorClass = 'text-scCyan';
    let nodeProgressColor = 'bg-scCyan shadow-[0_0_8px_#00ffd1]';
    if (nodeMemory && nodeMemory.limit > 0) {
        nodeMemoryUsage = Math.ceil(nodeMemory.used / nodeMemory.limit * 100);
        nodePart = `${nodeMemory.used.toFixed(0)} MB (${nodeMemoryUsage}%)`;
        if (nodeMemoryUsage > 85) {
            nodeColorClass = 'text-destructive';
            nodeProgressColor = 'bg-destructive shadow-[0_0_8px_var(--sc-danger)]';
        } else if (nodeMemoryUsage > 70) {
            nodeColorClass = 'text-warning';
            nodeProgressColor = 'bg-warning shadow-[0_0_8px_var(--sc-warn)]';
        }
    }

    const cardClass = "bg-[#0d101d]/60 border border-white/5 backdrop-blur-xl rounded-xl p-4 flex items-center justify-between shadow-md hover:shadow-lg hover:border-white/10 hover:translate-y-[-2px] transition-all duration-200 cursor-default";

    if (!displayData) {
        return (
            <>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className={cn(cardClass, "animate-pulse h-[4.5rem]")}>
                        <div className="flex flex-col gap-2 w-full">
                            <div className="h-4 bg-white/5 rounded w-1/3" />
                            <div className="h-6 bg-white/5 rounded w-2/3" />
                        </div>
                    </div>
                ))}
            </>
        );
    }

    return (
        <>
            {/* KPI 1: Uptime */}
            <div className={cn(cardClass, "relative overflow-hidden group")}>
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-scGreen/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Uptime (24h)</span>
                    <span className="text-xl font-bold text-white mt-1 font-mono tracking-tight">{uptimePart}</span>
                </div>
                <div className="flex items-center gap-2">
                    <UptimeCircle pct={uptimePct} />
                </div>
            </div>

            {/* KPI 2: Players */}
            <div className={cn(cardClass, "relative overflow-hidden group")}>
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-scCyan/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Median Players (24h)</span>
                    <span className="text-xl font-bold text-white mt-1 font-mono tracking-tight">{medianPlayerPart}</span>
                </div>
                <div className="p-2 rounded-lg bg-scCyan/5 border border-scCyan/10 text-scCyan">
                    <TrendingUpIcon size={18} className="animate-pulse" />
                </div>
            </div>

            {/* KPI 3: FXServer Memory */}
            <div className={cn(cardClass, "relative overflow-hidden group")}>
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-scPurple/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex flex-col flex-1 mr-4">
                    <span className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">FXServer RAM</span>
                    <span className="text-xl font-bold text-white mt-1 font-mono tracking-tight">{fxsPart}</span>
                    <div className="w-full bg-white/5 rounded-full h-1 mt-2 overflow-hidden">
                        <div 
                            className="bg-scPurple h-full rounded-full transition-all duration-500 shadow-[0_0_8px_#a78bfa]" 
                            style={{ width: fxsMemory ? `${Math.min(100, (fxsMemory / 8192) * 100)}%` : '0%' }}
                        />
                    </div>
                </div>
                <div className="p-2 rounded-lg bg-scPurple/5 border border-scPurple/10 text-scPurple">
                    <MemoryStickIcon size={18} />
                </div>
            </div>

            {/* KPI 4: Node.js Memory */}
            <div className={cn(cardClass, "relative overflow-hidden group")}>
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-scCyan/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex flex-col flex-1 mr-4">
                    <span className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Node.js RAM</span>
                    <span className={cn("text-xs font-bold mt-1 font-mono tracking-tight", nodeColorClass)}>{nodePart}</span>
                    <div className="w-full bg-white/5 rounded-full h-1 mt-2 overflow-hidden">
                        <div 
                            className={cn("h-full rounded-full transition-all duration-500", nodeProgressColor)}
                            style={{ width: `${Math.min(100, nodeMemoryUsage)}%` }}
                        />
                    </div>
                </div>
                <div className={cn("p-2 rounded-lg border", nodeMemoryUsage > 85 ? 'bg-destructive/5 border-destructive/10 text-destructive animate-bounce' : 'bg-scCyan/5 border-scCyan/10 text-scCyan')}>
                    {nodeMemoryUsage > 85 ? <ShieldAlertIcon size={18} /> : <TimerIcon size={18} />}
                </div>
            </div>
        </>
    );
}
